import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Download, Play, Scan, LockOpen, VolumeX, Wrench, CreditCard, Banknote, MapPin, Mail, Linkedin, BarChart3, Users, ArrowRight, Wifi, Plug, Check, Box, PoundSterling, TrendingUp, CalendarCheck, RefreshCw } from "lucide-react";
import VideoModal from "@/components/VideoModal";
import DeckEmailGate from "@/components/DeckEmailGate";
import { supabase } from "@/integrations/supabase/client";

import casestudy2 from "@/assets/deck/casestudy-2.jpg";
import casestudy3 from "@/assets/deck/casestudy-3.jpg";
import casestudy4 from "@/assets/deck/casestudy-4.jpg";
import podInSitu from "@/assets/deck/pod-in-situ.jpeg";
import tflLogo from "@/assets/deck/tfl-logo.png";
import qwipFounders from "@/assets/deck/qwip-founders.jpg";
import telephoneBox from "@/assets/deck/telephone-box-1926.png";
import qwipPodRender from "@/assets/deck/qwip-pod-render.png";
import timesArticle from "@/assets/deck/times-article-new.png";
import headshotZain from "@/assets/deck/headshot-zain.jpeg";
import headshotStiofan from "@/assets/deck/headshot-stiofan.png";
import headshotShreyas from "@/assets/deck/headshot-shreyas.png";
import logoMonzo from "@/assets/deck/logo-monzo-new.png";
import logoEF from "@/assets/deck/logo-ef-new.png";
import logoOlympics from "@/assets/deck/logo-olympics-new.png";
import logoAstraZeneca from "@/assets/deck/logo-astrazeneca-new.png";
import slide1Bg from "@/assets/deck/slide1-bg.jpg";
import logoBloomberg from "@/assets/deck/logo-bloomberg.png";
import logoAmazon from "@/assets/deck/logo-amazon.png";

// ─── Shared ─────────────────────────────────────────────────────
const QwipLogoMark = ({ light = false }: { light?: boolean }) => (
  <div className={`absolute top-6 right-6 font-funnel font-bold text-xl ${light ? "text-white" : "text-foreground"}`}>qwip</div>
);

// ─── Slide Components (landscape-only, no mobile breakpoints) ───

const SlideCover = () => {
  const [videoOpen, setVideoOpen] = useState(false);
  return (
    <div
      className="h-full flex flex-col justify-between px-16 py-14 relative"
      style={{ backgroundImage: `url(${slide1Bg})`, backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative z-10" />
      <div className="relative z-10">
        <h1 className="font-funnel font-bold text-[5.5rem] text-white leading-[0.95] tracking-tight max-w-5xl">
          the way we work<br />has changed.
        </h1>
         <p className="font-cormorant italic font-normal text-[3.2rem] text-white leading-[1.1] mt-4 max-w-4xl">
           but our cities haven't kept up.
        </p>
      </div>
      <div className="relative z-10 flex items-end justify-between">
        <div>
          <p className="font-funnel font-bold text-3xl text-white mb-1">qwip</p>
          <p className="text-white/60 font-sans text-base">bookable private pods in public spaces.</p>
        </div>
        <button
          onClick={() => setVideoOpen(true)}
          className="inline-flex items-center gap-3 border border-white/30 text-white px-6 py-3 rounded-full font-sans font-medium text-sm hover:bg-white hover:text-foreground transition-all"
        >
          <Play className="w-4 h-4" />
          watch our launch video
        </button>
      </div>
      <VideoModal isOpen={videoOpen} onClose={() => setVideoOpen(false)} />
    </div>
  );
};



const SlideProblem = () => (
  <div className="h-full flex flex-col items-center justify-center px-24 text-center" style={{ background: "linear-gradient(135deg, hsl(210 30% 12%), hsl(200 25% 16%), hsl(190 20% 14%))" }}>
    <QwipLogoMark light />
    <h2 className="font-funnel font-bold text-[4.5rem] text-white leading-[1] tracking-tight mb-6 max-w-5xl">
      300+ million people now work remotely worldwide.
    </h2>
    <p className="font-funnel text-[2.2rem] text-white/80 leading-[1.2] mb-8 max-w-4xl">
      but there's one problem <strong>every</strong> remote worker still faces.
    </p>
    <img src={timesArticle} alt="The Times — Is it acceptable to have a Zoom meeting in a café?" className="max-w-2xl w-full rounded-2xl border border-white/10 mb-10" />
    <p className="font-cormorant italic text-[2.8rem] text-white/90 leading-[1.15] max-w-4xl">
      where do you take that last-minute call?
    </p>
  </div>
);

const SlideHistoric = () => (
  <div className="h-full flex flex-col items-center justify-center px-20">
    <h2 className="font-funnel font-bold text-5xl text-foreground tracking-tight mb-12 text-center">
      the solution isn't new,{" "}
      <span className="font-cormorant italic font-normal">it's historic.</span>
    </h2>
    <div className="grid grid-cols-[1fr_auto_1fr] gap-12 items-end max-w-4xl w-full">
      <div className="flex flex-col items-center">
        <div className="w-64 h-80 rounded-3xl overflow-hidden mb-4">
          <img src={telephoneBox} alt="1926 telephone box" className="w-full h-full object-contain" />
        </div>
        <p className="font-funnel font-bold text-2xl text-foreground mb-1">the telephone box</p>
        <p className="text-muted-foreground font-sans text-lg">1926</p>
      </div>
      <div className="flex flex-col items-center pb-20">
        <div className="w-px h-48" style={{ background: "linear-gradient(to bottom, transparent, hsl(0 0% 70%), transparent)" }} />
      </div>
      <div className="flex flex-col items-center">
        <div className="w-64 h-80 rounded-3xl overflow-hidden mb-4">
          <img src={qwipPodRender} alt="2026 qwip pod" className="w-full h-full object-contain" />
        </div>
        <p className="font-funnel font-bold text-2xl text-foreground mb-1">the qwip pod</p>
        <p className="text-muted-foreground font-sans text-lg">2026</p>
      </div>
    </div>
  </div>
);

const SlideSolution = () => (
  <div className="grid grid-cols-2 h-full">
    <div className="flex flex-col justify-center px-16 py-12">
      <p className="font-funnel font-bold text-2xl text-primary mb-4">qwip</p>
      <h2 className="font-funnel text-5xl text-foreground leading-[0.95] tracking-tight mb-8">
        we place <span className="font-bold text-primary">bookable, soundproof pods</span> in <span className="font-bold text-primary">public spaces.</span>
      </h2>
      <p className="text-lg text-muted-foreground font-funnel leading-relaxed max-w-lg">
        For <strong className="text-foreground">calls</strong>, <strong className="text-foreground">focused work</strong>, or a <strong className="text-foreground">quiet moment</strong>.
      </p>
    </div>
    <div className="flex items-center justify-center p-8">
      <img src={podInSitu} alt="qwip pod in a café" className="w-full h-full object-cover rounded-3xl max-h-[80vh]" />
    </div>
  </div>
);

const SlideTraction = () => (
  <div className="grid grid-cols-2 h-full">
    <div className="flex flex-col justify-start px-16 pt-10 pb-6">
      <div className="flex items-center gap-6 mb-3">
        <span className="font-funnel font-bold text-[2.8rem] text-foreground">qwip</span>
        <div className="w-px h-14 bg-foreground/20" />
        <img src={tflLogo} alt="TfL" className="h-16 object-contain" />
      </div>
      <div className="flex items-center gap-2 mb-4">
        <MapPin className="w-4 h-4 text-muted-foreground" />
        <span className="font-sans text-sm text-muted-foreground font-medium">King's Cross Station</span>
      </div>
      <p className="text-base text-muted-foreground font-funnel leading-relaxed max-w-lg mb-4">
        this january, we trialled our first pod at King's Cross Station, the busiest transport hub in the UK (<strong className="text-foreground">221,000 daily visitors</strong>).
      </p>
      <div className="flex gap-3 mb-4 max-w-lg">
        {[
          { value: "$140", label: "revenue in one day" },
          { value: "~60%", label: "utilisation rate" },
        ].map((item) => (
          <div key={item.label} className="flex-1 bg-foreground text-primary-foreground rounded-2xl p-4">
            <p className="font-funnel font-bold text-2xl mb-1">{item.value}</p>
            <p className="text-primary-foreground/70 font-sans text-xs">{item.label}</p>
          </div>
        ))}
      </div>
      <p className="font-funnel font-bold text-xl text-foreground mb-3"><strong>londoners want qwip.</strong></p>
      <div className="bg-foreground/5 border border-foreground/10 rounded-2xl p-5">
        <p className="font-sans text-foreground text-sm leading-relaxed mb-2">
          "I had an hour before my Eurostar train to Paris and <strong>nowhere private</strong> to take a <strong>client call</strong>. This was <strong>exactly what I needed</strong>."
        </p>
        <p className="font-sans text-xs text-muted-foreground font-semibold uppercase tracking-wider">Adnaan, Consultant</p>
      </div>
    </div>
    <div className="flex items-center justify-center p-6">
      <img src={qwipFounders} alt="qwip founders at King's Cross" className="w-full h-full object-cover rounded-3xl" />
    </div>
  </div>
);

const SlideMarket = () => (
  <div className="flex flex-col h-full px-16 py-12 justify-center bg-primary relative">
    <QwipLogoMark light />
    <h2 className="font-funnel font-bold text-5xl text-white leading-[0.95] tracking-tight mb-2">
      the world is arriving at the same answer.
    </h2>
    <p className="font-cormorant italic font-normal text-4xl text-white/80 mb-12">we're connecting the dots.</p>
    <div className="grid grid-cols-3 gap-8">
      {[
        { img: casestudy2, country: "Japan", stat: "400+", description: "booths deployed across Japanese railway stations" },
        { img: casestudy3, country: "United States", stat: "50+", description: "booths across hotel lobbies and airports" },
        { img: casestudy4, country: "Middle East", stat: "30+", description: "booths across shopping centres, cafés, and fitness clubs" },
      ].map((item) => (
        <div key={item.country}>
          <div className="w-full aspect-[4/3] rounded-2xl overflow-hidden mb-3">
            <img src={item.img} alt={item.country} className="w-full h-full object-cover" />
          </div>
          <div className="flex items-center gap-2 mb-1">
            <MapPin className="w-4 h-4 text-white/60" />
            <p className="font-funnel font-bold text-white text-lg">{item.country}</p>
          </div>
          <p className="text-white/70 text-sm font-funnel leading-relaxed">{item.stat} {item.description}</p>
        </div>
      ))}
    </div>
  </div>
);

const HoverVideoStep = ({ step, index, direction, activeIndex, onVideoEnd }: { step: { icon: any; title: string; description: string | React.ReactNode; video: string }; index: number; direction: "left" | "right"; activeIndex: number; onVideoEnd: () => void }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isActive = activeIndex === index;

  useEffect(() => {
    if (videoRef.current) {
      if (isActive) {
        videoRef.current.currentTime = 0;
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  }, [isActive]);

  return (
    <motion.div
      initial={{ opacity: 0, x: direction === "left" ? -20 : 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.4 + index * 0.15 }}
      className="flex gap-4 items-start rounded-2xl p-3 -mx-3"
    >
      <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-foreground text-primary-foreground flex items-center justify-center">
        <step.icon className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <p className="font-funnel font-bold text-lg text-foreground mb-1">{step.title}</p>
        <p className="text-muted-foreground text-sm font-sans leading-relaxed">{step.description}</p>
        <motion.div
          animate={{ height: isActive ? "auto" : 0, opacity: isActive ? 1 : 0 }}
          initial={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden mt-3"
        >
          <div className="w-[150px] aspect-square rounded-xl overflow-hidden">
            <video
              ref={videoRef}
              muted
              playsInline
              preload="auto"
              onEnded={onVideoEnd}
              className="w-full h-full object-cover"
            >
              <source src={step.video} type="video/mp4" />
            </video>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

const SlideHowItWorks = () => {
  const [activeVideoIndex, setActiveVideoIndex] = useState(-1);

  useEffect(() => {
    const timer = setTimeout(() => setActiveVideoIndex(0), 600);
    return () => clearTimeout(timer);
  }, []);

  const handleVideoEnd = () => {
    setActiveVideoIndex((prev) => {
      if (prev < 2) return prev + 1;
      return -1;
    });
  };

  const userSteps = [
    { icon: Scan, title: "scan", description: "user scans the QR code on the pod to start the booking process.", video: "/videos/step-scan.mp4" },
    { icon: LockOpen, title: "unlock", description: "they get an access code, enter it on the numpad, and unlock the pod.", video: "/videos/step-unlock.mp4" },
    { icon: VolumeX, title: "focus", description: (<>the pod is all theirs. billed at <strong>$0.30/min</strong>, charged at the end of the session.</>), video: "/videos/step-focus.mp4" },
  ];

  const hostSteps = [
    { icon: Wrench, title: "we install", desc: (<>We deliver and install the pod (1m × 1m) at <strong>zero cost</strong> to the host location.</>) },
    { icon: CreditCard, title: "users pay", desc: "Users scan, book, and pay by the minute. We handle all payments and customer support." },
    { icon: Banknote, title: "revenue share", desc: (<>We operate on a <strong>revenue share model</strong>. The host earns a percentage of every booking, with zero operational burden.</>) },
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="px-14 pt-16 pb-4">
        <h2 className="font-funnel font-bold text-5xl text-foreground tracking-tight">how it works:</h2>
      </div>
      <div className="flex-1 flex items-center px-14">
        <div className="w-full grid grid-cols-[1fr_1px_1fr] gap-x-12">
          <div className="mb-4">
            <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground font-sans font-bold">for the user</p>
          </div>
          <div />
          <div className="mb-4">
            <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground font-sans font-bold">for the host</p>
          </div>

          {userSteps.map((step, i) => {
            const host = hostSteps[i];
            return (
              <div key={step.title} className="contents">
                <HoverVideoStep step={step} index={i} direction="left" activeIndex={activeVideoIndex} onVideoEnd={handleVideoEnd} />
                <div className="bg-foreground/10" />
                <div className="flex gap-4 items-start p-3">
                  <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-foreground text-primary-foreground flex items-center justify-center">
                    <host.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-funnel font-bold text-lg text-foreground mb-1">{host.title}</p>
                    <p className="text-muted-foreground text-sm font-sans leading-relaxed">{host.desc}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const SlideUnitEconomics = () => (
  <div className="h-full flex flex-col items-center justify-center px-20" style={{ background: "linear-gradient(135deg, hsl(158 33% 14%), hsl(158 33% 22%))" }}>
    <QwipLogoMark light />
    <h2 className="font-funnel font-bold text-5xl text-white tracking-tight mb-12 text-center">the pods pay for themselves.</h2>
    <div className="grid grid-cols-4 gap-6 max-w-5xl w-full">
      {[
        { value: "£180/mo", label: "total pod cost", icon: PoundSterling },
        { value: "£3,500/mo", label: "projected revenue\nper pod", icon: TrendingUp },
        { value: "one booking", label: "needed per day\nto break even", icon: CalendarCheck, prefix: "minimum" },
        { value: "~20x", label: "return on\nmonthly cost", icon: RefreshCw },
      ].map((item) => (
        <div key={item.label} className="bg-white text-foreground rounded-3xl p-8 flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
            <item.icon className="w-6 h-6 text-primary" />
          </div>
          {item.prefix && <p className="text-foreground/60 font-sans text-sm font-medium leading-snug">{item.prefix}</p>}
          <p className="font-funnel font-bold text-3xl mb-2">{item.value}</p>
          <p className="text-foreground/60 font-sans text-sm font-medium whitespace-pre-line leading-snug">{item.label}</p>
        </div>
      ))}
    </div>
  </div>
);

const SlideRevenue = () => (
  <div className="h-full flex flex-col items-center justify-center px-20">
    <h2 className="font-funnel font-bold text-5xl text-foreground tracking-tight mb-12 text-center">three ways every pod makes money.</h2>
    <div className="grid grid-cols-3 gap-8 max-w-5xl w-full">
      {[
        { title: "Per-minute bookings", desc: "Core revenue. Users pay $0.30/min for private use. Billed automatically at the end of each session.", tag: "primary revenue", icon: CreditCard },
        { title: "Brand placements on pods", desc: (<>External screen and interior branding placements. Already received interest from <strong className="text-foreground">Deel</strong>.</>), tag: "advertising", icon: BarChart3 },
        { title: "Subscription tier", desc: "Monthly pass for frequent users. Discounted rate and special perks.", tag: "recurring", icon: Users },
      ].map((item) => (
        <div key={item.title} className="border border-foreground/10 rounded-3xl p-8 flex flex-col">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
            <item.icon className="w-6 h-6 text-primary" />
          </div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-primary font-sans font-bold mb-2">{item.tag}</p>
          <p className="font-funnel font-bold text-2xl text-foreground mb-3">{item.title}</p>
          <p className="text-muted-foreground font-sans text-sm leading-relaxed">{item.desc}</p>
        </div>
      ))}
    </div>
  </div>
);

const SlideVision = () => (
  <div className="h-full flex flex-col justify-center px-20 py-12" style={{ background: "linear-gradient(135deg, hsl(158 33% 12%), hsl(158 33% 18%), hsl(158 20% 22%))" }}>
    <QwipLogoMark light />
    <h2 className="font-funnel font-bold text-[5.5rem] text-white tracking-tight leading-[0.95] mb-3">
      every space will have a pod.
    </h2>
    <p className="font-cormorant italic font-normal text-[4rem] text-white/90 leading-[1] mb-14">
      the question is <span className="underline">whose</span>?
    </p>
    <div className="flex items-center gap-4 mb-14 flex-wrap">
      {[
        { icon: Wifi, label: "WiFi", highlight: false },
        { icon: Plug, label: "Power outlets", highlight: false },
        { icon: Box, label: "Soundproof Pod", highlight: true },
      ].map((amenity) => (
        <div key={amenity.label} className={`flex items-center gap-2.5 rounded-xl px-4 py-3 ${amenity.highlight ? "bg-white text-foreground border-2 border-white" : "bg-white/10 text-white/70 border border-white/10"}`}>
          <Check className={`w-4 h-4 flex-shrink-0 ${amenity.highlight ? "text-primary" : "text-white/50"}`} />
          <amenity.icon className={`w-4 h-4 ${amenity.highlight ? "text-primary" : "text-white/50"}`} />
          <span className={`font-sans text-sm font-medium ${amenity.highlight ? "text-foreground font-bold" : ""}`}>{amenity.label}</span>
        </div>
      ))}
    </div>
    <p className="text-white/80 font-funnel text-[2.5rem] leading-snug max-w-5xl">
      Bookable pods are becoming part of standard infrastructure.
      <br /><strong className="text-white font-bold">qwip is building the network to own them, worldwide.</strong>
    </p>
  </div>
);

const SlideTeam = () => (
  <div className="h-full flex flex-col items-center justify-center px-20">
    <h2 className="font-funnel font-bold text-5xl text-foreground tracking-tight mb-2 text-center">built by friends who've lived the problem</h2>
    <p className="font-cormorant italic font-medium text-[1.8rem] text-muted-foreground mb-10 text-center">and have shipped products and brands that have reached millions.</p>
    <div className="relative grid grid-cols-3 gap-10 max-w-5xl w-full">
      <div className="flex flex-col items-center text-center">
        <img src={headshotZain} alt="Zain Hussain" className="w-28 h-28 rounded-full object-cover mb-5" />
        <p className="font-funnel font-bold text-2xl text-foreground mb-0">Zain Hussain</p>
        <div className="flex items-center gap-2 mt-1 mb-4">
          <p className="text-xs uppercase tracking-[0.25em] text-primary font-sans font-bold">CEO</p>
          <div className="w-px h-3 bg-foreground/20" />
          <a href="https://www.linkedin.com/in/zain-hussain" target="_blank" rel="noopener noreferrer" className="text-foreground/40 hover:text-foreground transition-colors"><Linkedin className="w-4 h-4" /></a>
        </div>
        <div className="flex flex-col gap-1.5 mb-4 text-left w-full">
          <p className="text-muted-foreground font-sans text-sm">Second-time founder</p>
          <p className="text-muted-foreground font-sans text-sm">Previously backed by Entrepreneur First, angels from a16z</p>
           <p className="text-muted-foreground font-sans text-sm">Early employee at Monzo (4+ yrs), led scam prevention ops (300+ team)</p>
           <p className="text-muted-foreground font-sans text-sm">Lawyer. Research adopted by the Law Commission of England & Wales.</p>
         </div>
          <div className="h-14 flex items-center justify-center gap-5 mt-auto pt-3">
           <img src={logoMonzo} alt="Monzo" className="h-10 object-contain opacity-60" />
           <img src={logoEF} alt="Entrepreneur First" className="h-10 object-contain opacity-60" />
          </div>
      </div>
      <div className="flex flex-col items-center text-center">
        <img src={headshotStiofan} alt="Stiofán Folan-Hasici" className="w-28 h-28 rounded-full object-cover mb-5" />
        <p className="font-funnel font-bold text-2xl text-foreground mb-0">Stiofán Folan-Hasici</p>
        <div className="flex items-center gap-2 mt-1 mb-4">
          <p className="text-xs uppercase tracking-[0.25em] text-primary font-sans font-bold">COO</p>
          <div className="w-px h-3 bg-foreground/20" />
          <a href="https://www.linkedin.com/in/stiofanfh/" target="_blank" rel="noopener noreferrer" className="text-foreground/40 hover:text-foreground transition-colors"><Linkedin className="w-4 h-4" /></a>
        </div>
        <div className="flex flex-col gap-1.5 mb-4 text-left w-full">
          <p className="text-muted-foreground font-sans text-sm">Designer and brand strategist</p>
          <p className="text-muted-foreground font-sans text-sm">Built brands for Olympic athletes and AstraZeneca</p>
          <p className="text-muted-foreground font-sans text-sm">Grew brands from 0 to 1M+ followers and 2,300+ paid community members in under a year</p>
        </div>
          <div className="h-14 flex items-center justify-center gap-5 mt-auto pt-3">
           <img src={logoOlympics} alt="Olympics" className="h-10 object-contain opacity-60" />
           <img src={logoAstraZeneca} alt="AstraZeneca" className="h-10 object-contain opacity-60" />
          </div>
      </div>
      <div className="flex flex-col items-center text-center">
        <img src={headshotShreyas} alt="Shreyas Saxena" className="w-28 h-28 rounded-full object-cover mb-5" />
        <p className="font-funnel font-bold text-2xl text-foreground mb-0">Shreyas Saxena</p>
        <div className="flex items-center gap-2 mt-1 mb-4">
          <p className="text-xs uppercase tracking-[0.25em] text-primary font-sans font-bold">CTO</p>
          <div className="w-px h-3 bg-foreground/20" />
          <a href="#" target="_blank" rel="noopener noreferrer" className="text-foreground/40 hover:text-foreground transition-colors"><Linkedin className="w-4 h-4" /></a>
        </div>
        <div className="flex flex-col gap-1.5 mb-4 text-left w-full">
          <p className="text-muted-foreground font-sans text-sm">Engineer at Bloomberg, ex-Amazon</p>
          <p className="text-muted-foreground font-sans text-sm">Built financial data products used by top-tier institutions globally</p>
          <p className="text-muted-foreground font-sans text-sm">Replaced legacy Bloomberg Terminal workflows, cutting friction by 50%</p>
        </div>
          <div className="h-14 flex items-center justify-center gap-5 mt-auto pt-3">
           <img src={logoBloomberg} alt="Bloomberg" className="h-10 object-contain opacity-60" />
           <img src={logoAmazon} alt="Amazon" className="h-10 object-contain opacity-60" />
          </div>
      </div>
    </div>
  </div>
);

const SlideRoadmap = () => (
  <div className="h-full flex flex-col items-center justify-center px-20">
    <h2 className="font-funnel font-bold text-5xl text-foreground tracking-tight mb-2 text-center">the platform to book any pod, anywhere.</h2>
    <p className="font-cormorant italic font-normal text-[2.2rem] text-muted-foreground mb-14 text-center">here's how we get there.</p>
    <div className="relative max-w-6xl w-full">
      <div className="absolute top-6 left-0 right-0 h-0.5 bg-foreground/10" />
      <div className="grid grid-cols-4 gap-6">
        {[
          { quarter: "Q1 2026", title: "Launch and pilot.", desc: "Pilot pod deployment at King's Cross with TfL.", active: true },
          { quarter: "Q2 2026", title: "10 pods across London.", desc: "10 permanent installations across London. Transit hubs, fitness clubs, cafes.", active: false },
          { quarter: "Q3 2026", title: "Expand beyond London.", desc: "50+ pods across the UK. Transit hubs, fitness clubs, and shopping centres.", active: false },
          { quarter: "Q4 2026", title: "Expand internationally.", desc: (<>International expansion to <strong className="text-foreground">San Francisco</strong>, <strong className="text-foreground">New York City</strong> and beyond.</>), active: false },
        ].map((item) => (
          <div key={item.quarter} className="flex flex-col items-center text-center">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-6 ${item.active ? "bg-primary text-white" : "bg-foreground/10 text-foreground/40"}`}>
              <ArrowRight className="w-5 h-5" />
            </div>
            <p className={`text-xs uppercase tracking-[0.25em] font-sans font-bold mb-2 ${item.active ? "text-primary" : "text-muted-foreground"}`}>{item.quarter}</p>
            <p className="font-funnel font-bold text-lg text-foreground mb-2">{item.title}</p>
            <p className="text-muted-foreground font-sans text-xs leading-relaxed">{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const SlideClose = () => (
  <div className="h-full flex flex-col items-center justify-center text-center px-24" style={{ background: "linear-gradient(135deg, hsl(158 33% 14%), hsl(158 33% 22%), hsl(158 20% 28%))" }}>
    <p className="font-funnel font-bold text-9xl text-white mb-6">qwip</p>
    <p className="text-2xl text-white/70 font-funnel mb-10">the public phone booth, upgraded.</p>
    <div className="flex items-center gap-3 text-white/80 font-sans text-lg">
      <Mail className="w-5 h-5" />
      hello@qwip.co.uk
    </div>
  </div>
);

// ─── Slides Array ────────────────────────────────────────────────
const slides = [
  SlideCover,
  SlideProblem,
  SlideHistoric,
  SlideSolution,
  SlideHowItWorks,
  SlideTraction,
  SlideTeam,
  SlideMarket,
  SlideUnitEconomics,
  SlideRevenue,
  SlideRoadmap,
  SlideVision,
  SlideClose,
];

// Dark slide indices (for arrow/dot color)
const darkSlideIndices = new Set([0, 1, 7, 8, 11, 12]);

// ─── Fundraising Deck Viewer ─────────────────────────────────────
const FundraisingDeck = ({ skipEmailGate = false }: { skipEmailGate?: boolean }) => {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  const [showGate, setShowGate] = useState(!skipEmailGate);
  const [viewId, setViewId] = useState("");
  const startTimeRef = useRef(Date.now());
  const slidesVisitedRef = useRef<Set<number>>(new Set([0]));

  const goTo = useCallback((index: number) => {
    if (index < 0 || index >= slides.length) return;
    setDirection(index > current ? 1 : -1);
    setCurrent(index);
    slidesVisitedRef.current.add(index);
  }, [current]);

  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  // Heartbeat
  useEffect(() => {
    if (!viewId) return;
    const interval = setInterval(async () => {
      const duration = Math.round((Date.now() - startTimeRef.current) / 1000);
      const slidesArr = Array.from(slidesVisitedRef.current);
      await supabase
        .from("deck_views")
        .update({
          last_active_at: new Date().toISOString(),
          duration_seconds: duration,
          slides_viewed: slidesArr,
          total_slides_viewed: slidesArr.length,
        })
        .eq("id", viewId);
    }, 10000);
    return () => clearInterval(interval);
  }, [viewId]);

  // Final update on unmount
  useEffect(() => {
    if (!viewId) return;
    const handleUnload = () => {
      const duration = Math.round((Date.now() - startTimeRef.current) / 1000);
      const slidesArr = Array.from(slidesVisitedRef.current);
      navigator.sendBeacon?.(
        `https://pxurtuktvoeaytlozzue.supabase.co/rest/v1/deck_views?id=eq.${viewId}`,
        JSON.stringify({
          last_active_at: new Date().toISOString(),
          duration_seconds: duration,
          slides_viewed: slidesArr,
          total_slides_viewed: slidesArr.length,
        })
      );
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [viewId]);

  const handleEmailSubmit = (_email: string, id: string) => {
    setViewId(id);
    startTimeRef.current = Date.now();
    setShowGate(false);
  };

  // Keyboard navigation
  useEffect(() => {
    if (showGate) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); next(); }
      if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
      if (e.key === "f" || e.key === "F") {
        if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
        else document.exitFullscreen?.();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [next, prev, showGate]);

  // Touch/swipe
  useEffect(() => {
    if (showGate) return;
    let startX = 0;
    const handleTouchStart = (e: TouchEvent) => { startX = e.touches[0].clientX; };
    const handleTouchEnd = (e: TouchEvent) => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) { diff > 0 ? next() : prev(); }
    };
    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchend", handleTouchEnd);
    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [next, prev, showGate]);

  const CurrentSlide = slides[current];
  const isDark = darkSlideIndices.has(current);
  const arrowBg = isDark ? "bg-white/20 hover:bg-white/40" : "bg-foreground/10 hover:bg-foreground/20";
  const arrowIcon = isDark ? "text-white" : "text-foreground";
  const dotActive = isDark ? "bg-white" : "bg-foreground";
  const dotInactive = isDark ? "bg-white/50 hover:bg-white/70" : "bg-foreground/20 hover:bg-foreground/40";

  const variants = {
    enter: { opacity: 0 },
    center: { opacity: 1 },
    exit: { opacity: 0 },
  };

  return (
    <div className="h-screen w-screen bg-background overflow-hidden relative select-none">
      {showGate && <DeckEmailGate onSubmit={handleEmailSubmit} deckType="investor" />}

      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={current}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.6, ease: "easeInOut" }}
          className="absolute inset-0"
        >
          <CurrentSlide />
        </motion.div>
      </AnimatePresence>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-foreground/5">
        <motion.div
          className="h-full bg-foreground"
          animate={{ width: `${((current + 1) / slides.length) * 100}%` }}
          transition={{ duration: 0.4 }}
        />
      </div>

      {/* Dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4">
        <div className="flex gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${i === current ? `${dotActive} w-6 shadow-md` : dotInactive}`}
            />
          ))}
        </div>
      </div>

      {/* Arrows */}
      {current > 0 && (
        <button onClick={prev} className={`absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full ${arrowBg} flex items-center justify-center transition-colors backdrop-blur-sm`}>
          <ChevronLeft className={`w-5 h-5 ${arrowIcon}`} />
        </button>
      )}
      {current < slides.length - 1 && (
        <button onClick={next} className={`absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full ${arrowBg} flex items-center justify-center transition-colors backdrop-blur-sm`}>
          <ChevronRight className={`w-5 h-5 ${arrowIcon}`} />
        </button>
      )}

      {/* Slide counter + download */}
      <div className="absolute top-6 right-6 flex items-center gap-3">
        <button
          onClick={() => window.open("/investor-deck", "_blank")}
          className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${isDark ? "bg-white/10 hover:bg-white/20 text-white/50 hover:text-white" : "bg-foreground/5 hover:bg-foreground/10 text-muted-foreground/50 hover:text-foreground"}`}
          title="Download as PDF"
        >
          <Download className="w-3.5 h-3.5" />
        </button>
        <span className="text-xs text-muted-foreground/50 font-sans">
          {current + 1} / {slides.length}
        </span>
      </div>
    </div>
  );
};

export default FundraisingDeck;
